/**
 * Qualitas AML compliance constants.
 *
 * Disclaimer text is the Spanish wording provided by Qualitas Compañía de
 * Seguros, S.A. Spanish is the legally authoritative version.
 */

/** Bump when Qualitas changes the legal wording — stored on each acceptance. */
export const QUALITAS_AML_DISCLAIMER_VERSION = 'qualitas-aml-2026-07-20'

export const QUALITAS_AML_PARTNER = {
  displayName: 'Quálitas',
  legalName: 'Quálitas Compañía de Seguros, S.A.',
  shortName: 'Quálitas',
} as const

/**
 * Exact disclaimer provided by Qualitas (ES authoritative).
 * Split into paragraphs for readable scroll UI; content is not paraphrased.
 */
export const QUALITAS_AML_DISCLAIMER_PARAGRAPHS_ES: readonly string[] = [
  'EL CLIENTE AUTORIZA A QUÁLITAS COMPAÑÍA DE SEGUROS, S.A., A REALIZAR LOS ANÁLISIS, VERIFICACIONES E INVESTIGACIONES QUE CONSIDEREN NECESARIAS Y PERTINENTES A TRAVÉS DE LOS MEDIOS NECESARIOS PARA VERIFICAR LA INFORMACIÓN PROPORCIONADA; ASÍ MISMO, A INFORMAR DE MANERA INMEDIATA Y DOCUMENTADA A LA AUTORIDAD COMPETENTE EN CASOS DE INVESTIGACIÓN O CUANDO SE DETECTARE OPERACIONES INUSUALES O TRANSACCIONES SOSPECHOSAS. EL CLIENTE DECLARA QUE GARANTIZA LA VERACIDAD Y EXACTITUD DE LA INFORMACIÓN PROPORCIONADA, QUE EN CASO DE DECLARAR O PROPORCIONAR INFORMACIÓN NO VERAZ, INEXACTA O FALSA, INCURRIRÍA EN RESPONSABILIDAD QUE LE PODRÁ SER DEDUCIDA POR LAS AUTORIDADES COMPETENTES DE LA REPÚBLICA DE EL SALVADOR.',
  'EL CLIENTE SE COMPROMETE TAMBIÉN A ACTUALIZAR LA DOCUMENTACIÓN CUANDO LE SEA REQUERIDA POR QUÁLITAS COMPAÑÍA DE SEGUROS, S.A., COMUNICANDO DE MANERA INMEDIATA CUALQUIER CAMBIO EN LA MISMA.',
  'EN ADICIÓN, EL CLIENTE DECLARA QUE SU NOMBRE NO ESTÁ INCLUIDO EN LA OFICINA DE CONTROL DE ACTIVOS EN EL EXTERIOR (OFAC), O EN CUALQUIER OTRA LISTA DE SIMILAR NATURALEZA O EN ALGUNA DE CARÁCTER NACIONAL Y/O INTERNACIONAL EN LA QUE SE PUBLIQUEN LOS DATOS DE LAS PERSONAS A QUIENES SE LES HAYA INICIADO PROCESO JUDICIAL O QUE HAYAN SIDO SANCIONADAS Y/O CONDENADAS POR LAS AUTORIDADES NACIONALES E INTERNACIONALES DE MANERA DIRECTA O INDIRECTA CON ACTIVIDADES ILEGALES TALES COMO NARCOTRÁFICO, TERRORISMO O SU FINANCIACIÓN, LAVADO DE DINERO Y ACTIVOS, TRÁFICO DE ESTUPEFACIENTES Y TODOS LOS DEMÁS DELITOS CONEXOS DE CONFORMIDAD A LO ESTABLECIDO EN EL ARTÍCULO SEIS DE LA LEY CONTRA EL LAVADO DE DINERO Y DE ACTIVOS.',
] as const

export const QUALITAS_AML_DISCLAIMER_PARAGRAPHS_EN: readonly string[] = [
  'THE CLIENT AUTHORIZES QUÁLITAS COMPAÑÍA DE SEGUROS, S.A. TO PERFORM THE ANALYSES, VERIFICATIONS, AND INVESTIGATIONS IT DEEMS NECESSARY AND RELEVANT THROUGH THE MEANS REQUIRED TO VERIFY THE INFORMATION PROVIDED; AND LIKEWISE TO PROMPTLY AND DOCUMENTED REPORT TO THE COMPETENT AUTHORITY IN INVESTIGATION CASES OR WHEN UNUSUAL OPERATIONS OR SUSPICIOUS TRANSACTIONS ARE DETECTED. THE CLIENT DECLARES THAT THEY GUARANTEE THE TRUTHFULNESS AND ACCURACY OF THE INFORMATION PROVIDED, AND THAT IF THEY DECLARE OR PROVIDE UNTRUE, INACCURATE, OR FALSE INFORMATION, THEY WOULD INCUR LIABILITY THAT MAY BE PURSUED BY THE COMPETENT AUTHORITIES OF THE REPUBLIC OF EL SALVADOR.',
  'THE CLIENT ALSO UNDERTAKES TO UPDATE THE DOCUMENTATION WHEN REQUIRED BY QUÁLITAS COMPAÑÍA DE SEGUROS, S.A., PROMPTLY COMMUNICATING ANY CHANGE TO IT.',
  'IN ADDITION, THE CLIENT DECLARES THAT THEIR NAME IS NOT INCLUDED ON THE OFFICE OF FOREIGN ASSETS CONTROL (OFAC) LIST, OR ON ANY OTHER LIST OF A SIMILAR NATURE OR OF A NATIONAL AND/OR INTERNATIONAL CHARACTER PUBLISHING DATA OF PERSONS AGAINST WHOM JUDICIAL PROCEEDINGS HAVE BEEN INITIATED OR WHO HAVE BEEN SANCTIONED AND/OR CONVICTED BY NATIONAL AND INTERNATIONAL AUTHORITIES, DIRECTLY OR INDIRECTLY, IN CONNECTION WITH ILLEGAL ACTIVITIES SUCH AS DRUG TRAFFICKING, TERRORISM OR ITS FINANCING, MONEY AND ASSET LAUNDERING, NARCOTICS TRAFFICKING, AND ALL RELATED CRIMES IN ACCORDANCE WITH ARTICLE SIX OF THE LAW AGAINST MONEY AND ASSET LAUNDERING.',
] as const

/**
 * Option labels are NOT constants: every choice the user reads comes from
 * `aml.income_option.*`, `aml.fund_option.*`, `aml.gender_option.*` and
 * `aml.activity_option.*` in the locale files, so both languages stay in
 * parity and no user-visible string is frozen in TypeScript.
 */

/**
 * Version of the international-transfer authorization the renter reads on the
 * AML screen (`aml.cross_border_*` in both locales).
 *
 * D.L. 144/2024 Art. 44 requires the consent for an international transfer to
 * be prior and express, and Art. 54 puts the burden of proving it on Tago —
 * so what we store has to identify the exact wording the person saw.
 *
 * Deliberately NOT derived from `QUALITAS_AML_DISCLAIMER_VERSION`: that string
 * versions the insurer's own text, and inheriting it would claim the user read
 * a wording they never saw the next time the insurer bumps theirs. Bump this
 * one — and only this one — when the `aml.cross_border_*` copy changes.
 */
export const AML_CROSS_BORDER_CONSENT_VERSION = 'tago-cross-border-2026-08-01'

/**
 * Published wording backing the authorization; stored with each grant.
 *
 * The locale segment is REQUIRED. tago.rent serves legal documents from
 * `app/[locale]/legal/[slug]`, so `/legal/privacy-policy` — what this used to
 * point at — returns 404, while `/es/legal/privacy-policy` returns the
 * document. Art. 54 D.L. 144/2024 puts the burden of proving consent on Tago,
 * and a 404 proves nothing.
 *
 * `/legal/:slug` now redirects to `/es/legal/:slug` (apps/marketing/next.config.ts)
 * so grants already stored against the old shape still resolve.
 */
export const AML_CROSS_BORDER_CONSENT_URL = 'https://tago.rent/es/legal/privacy-policy'

/**
 * `legal_acceptances.document_type` for the insurer's declaration.
 *
 * Written by the verification step and read back by
 * `verificationService.getOverview`, so it has to be one literal in one place —
 * a typo on either side silently reopens a step the user already completed.
 */
export const QUALITAS_AML_DISCLAIMER_DOCUMENT_TYPE = 'qualitas_aml_disclaimer'

/**
 * Where the insurer's declaration is published, stored alongside each
 * acceptance in `legal_acceptances.document_url`.
 *
 * The URL is a convenience pointer, NOT the audit record. What Qualitas and
 * Art. 54 D.L. 144/2024 actually need is the signature, and that lives in the
 * row: `profile_id` + `document_version` + server-side `accepted_at`, under an
 * INSERT-only RLS policy (`WITH CHECK profile_id = auth.uid()`, no UPDATE or
 * DELETE policy) so a stored acceptance cannot be altered or backdated. The
 * wording that `document_version` identifies is frozen in
 * `QUALITAS_AML_DISCLAIMER_PARAGRAPHS_ES/EN` below and in git history — which
 * is stronger evidence than a URL, because a page can be edited in place and a
 * pinned constant cannot.
 *
 * The page is not published yet (`qualitas-aml-disclaimer` is not in
 * `LEGAL_SLUGS`). That is worth fixing so a person can re-read what they
 * signed, but it does not weaken the audit trail. `document_url` is NOT NULL,
 * so this stays in the locale-prefixed shape and resolves the moment the slug
 * is added to `@tago/legal`.
 */
export const QUALITAS_AML_DISCLAIMER_URL = 'https://tago.rent/es/legal/qualitas-aml-disclaimer'

/** ISO2 for El Salvador — origin country only asked when nationality ≠ SV. */
export const AML_SV_ISO2 = 'SV'

/** Scroll threshold (px from bottom) to unlock Accept on the disclaimer. */
export const AML_DISCLAIMER_SCROLL_THRESHOLD_PX = 28
